import { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { useTranslation } from '../i18n/LanguageContext';
import { MapPin, Calendar, ArrowLeft, Heart, Globe, Home, Flag, Briefcase, Flame, Eye, Share2, Copy, Check, ExternalLink, Pencil, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { CondolenceDialog } from '../components/CondolenceForm';
import CondolenceList from '../components/CondolenceList';
import MemorialPlaceholder from '../components/MemorialPlaceholder';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ─── SEO helpers ──────────────────────────────────────────────────────────────
function setMeta(attrName, attrVal, content) {
  let el = document.querySelector(`meta[${attrName}="${attrVal}"]`);
  if (!el) { el = document.createElement('meta'); el.setAttribute(attrName, attrVal); document.head.appendChild(el); }
  el.setAttribute('content', content);
}
function setJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) { el = document.createElement('script'); el.type = 'application/ld+json'; el.id = id; document.head.appendChild(el); }
  el.textContent = JSON.stringify(data);
}
function setCanonical(href) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) { el = document.createElement('link'); el.rel = 'canonical'; document.head.appendChild(el); }
  el.href = href;
}

// Meta title templates by language
const META_TEMPLATES = {
  de: {
    title: (name, location, type) => {
      if (type === 'persoenlichkeit') return `${name} – Nachruf und Gedenkseite | verstorben.ch`;
      if (type === 'publiziert' && location) return `Todesanzeige ${name} aus ${location} | verstorben.ch`;
      return `Gedenkseite ${name}${location ? ` aus ${location}` : ''} | verstorben.ch`;
    },
    description: (name, location, dates, bio) => {
      if (bio) return `${bio.substring(0, 140).trim()}… – Gedenkseite auf verstorben.ch.`;
      return `Gedenkseite für ${name}${location ? ` aus ${location}` : ''}${dates ? `. ${dates}` : ''}. Teilen Sie Erinnerungen und Beileidsbekundungen.`;
    },
  },
  fr: {
    title: (name, location) => `Page commémorative ${name}${location ? ` de ${location}` : ''} | verstorben.ch`,
    description: (name, location, dates) => `Page commémorative pour ${name}${location ? ` de ${location}` : ''}${dates ? `. ${dates}` : ''}. Partagez vos souvenirs et condoléances.`,
  },
  it: {
    title: (name, location) => `Pagina commemorativa ${name}${location ? ` di ${location}` : ''} | verstorben.ch`,
    description: (name, location, dates) => `Pagina commemorativa per ${name}${location ? ` di ${location}` : ''}${dates ? `. ${dates}` : ''}. Condividete ricordi e condoglianze.`,
  },
};

// Get language from URL path
const getLanguageFromPath = (pathname) => {
  if (pathname.startsWith('/avis-de-deces')) return 'fr';
  if (pathname.startsWith('/annunci-funebri')) return 'it';
  return 'de';
};

// Generate SEO-friendly URL for an inserat
export const generateMemorialUrl = (inserat, lang = 'de') => {
  if (!inserat || !inserat.slug) {
    return `/gedenken/${inserat?.id || ''}`;
  }
  
  const kanton = inserat.kanton_code?.toLowerCase() || 'ch';
  const basePaths = {
    de: '/todesanzeigen',
    fr: '/avis-de-deces',
    it: '/annunci-funebri',
  };
  
  return `${basePaths[lang] || basePaths.de}/${kanton}/${inserat.slug}`;
};

// Zodiac sign SVG icons mapping
const ZODIAC_ICONS = {
  'Widder': (
    <svg viewBox="0 0 100 100" fill="currentColor" className="w-full h-full">
      <path d="M25,75 C25,45 35,30 50,30 C65,30 75,45 75,75 M35,30 C35,20 42,15 50,15 C58,15 65,20 65,30 M25,75 L25,85 M75,75 L75,85" stroke="currentColor" strokeWidth="4" fill="none"/>
    </svg>
  ),
  'Stier': (
    <svg viewBox="0 0 100 100" fill="currentColor" className="w-full h-full">
      <circle cx="50" cy="55" r="25" stroke="currentColor" strokeWidth="4" fill="none"/>
      <path d="M25,35 C20,25 25,15 35,20 M75,35 C80,25 75,15 65,20" stroke="currentColor" strokeWidth="4" fill="none"/>
    </svg>
  ),
  'Zwillinge': (
    <svg viewBox="0 0 100 100" fill="currentColor" className="w-full h-full">
      <path d="M25,20 L75,20 M25,80 L75,80 M35,20 L35,80 M65,20 L65,80" stroke="currentColor" strokeWidth="4" fill="none"/>
    </svg>
  ),
  'Krebs': (
    <svg viewBox="0 0 100 100" fill="currentColor" className="w-full h-full">
      <circle cx="35" cy="40" r="15" stroke="currentColor" strokeWidth="4" fill="none"/>
      <circle cx="65" cy="60" r="15" stroke="currentColor" strokeWidth="4" fill="none"/>
      <path d="M45,30 C70,25 80,45 75,60 M55,70 C30,75 20,55 25,40" stroke="currentColor" strokeWidth="3" fill="none"/>
    </svg>
  ),
  'Loewe': (
    <svg viewBox="0 0 100 100" fill="currentColor" className="w-full h-full">
      <circle cx="40" cy="40" r="20" stroke="currentColor" strokeWidth="4" fill="none"/>
      <path d="M55,55 C70,70 80,75 85,70 C90,65 85,55 75,50" stroke="currentColor" strokeWidth="4" fill="none"/>
    </svg>
  ),
  'Jungfrau': (
    <svg viewBox="0 0 100 100" fill="currentColor" className="w-full h-full">
      <path d="M25,80 L25,30 C25,20 35,15 40,25 L40,80 M40,30 C40,20 50,15 55,25 L55,80 M55,30 C55,20 65,15 70,25 L70,55 C70,65 80,70 85,65" stroke="currentColor" strokeWidth="4" fill="none"/>
    </svg>
  ),
  'Waage': (
    <svg viewBox="0 0 100 100" fill="currentColor" className="w-full h-full">
      <path d="M20,55 L80,55 M50,55 L50,80 M20,80 L80,80 M25,45 C25,35 35,30 50,30 C65,30 75,35 75,45" stroke="currentColor" strokeWidth="4" fill="none"/>
    </svg>
  ),
  'Skorpion': (
    <svg viewBox="0 0 100 100" fill="currentColor" className="w-full h-full">
      <path d="M25,80 L25,30 C25,20 35,15 40,25 L40,80 M40,30 C40,20 50,15 55,25 L55,80 M55,30 C55,20 65,15 70,25 L70,55 C70,70 85,75 90,65 L85,60 M90,65 L85,70" stroke="currentColor" strokeWidth="4" fill="none"/>
    </svg>
  ),
  'Schuetze': (
    <svg viewBox="0 0 100 100" fill="currentColor" className="w-full h-full">
      <path d="M25,75 L75,25 M75,25 L55,25 M75,25 L75,45 M35,55 L55,35" stroke="currentColor" strokeWidth="4" fill="none"/>
    </svg>
  ),
  'Steinbock': (
    <svg viewBox="0 0 100 100" fill="currentColor" className="w-full h-full">
      <path d="M30,30 C30,20 40,15 50,20 L50,60 C50,75 60,80 70,75 C80,70 75,55 65,60 C55,65 60,80 75,85" stroke="currentColor" strokeWidth="4" fill="none"/>
    </svg>
  ),
  'Wassermann': (
    <svg viewBox="0 0 100 100" fill="currentColor" className="w-full h-full">
      <path d="M20,40 C30,30 40,50 50,40 C60,30 70,50 80,40 M20,60 C30,50 40,70 50,60 C60,50 70,70 80,60" stroke="currentColor" strokeWidth="4" fill="none"/>
    </svg>
  ),
  'Fische': (
    <svg viewBox="0 0 100 100" fill="currentColor" className="w-full h-full">
      <path d="M20,50 C35,35 45,35 50,50 C45,65 35,65 20,50 M80,50 C65,35 55,35 50,50 C55,65 65,65 80,50 M50,25 L50,75" stroke="currentColor" strokeWidth="4" fill="none"/>
    </svg>
  ),
};
function PubliziertHero({ inserat }) {
  const { t } = useTranslation();
  return (
    <div className="memorial-hero-publiziert" data-testid="memorial-hero-publiziert">
      <div className="max-w-3xl mx-auto px-4 text-center">
        <p className="text-xs text-[#94A3B8] uppercase tracking-[0.2em] mb-4">{t('memorial.official_notice')}</p>
        <div className="w-20 h-20 md:w-24 md:h-24 rounded-full overflow-hidden mx-auto mb-6 shadow-sm">
          <MemorialPlaceholder 
            vorname={inserat.vorname} 
            nachname={inserat.nachname} 
            kantonCode={inserat.kanton_code}
            sternzeichen={inserat.sternzeichen_name}
            size="md"
            className="w-full h-full rounded-full"
          />
        </div>
        <h1 className="font-serif-accent text-3xl md:text-4xl text-[#1E293B]">
          {inserat.vorname} {inserat.nachname}
        </h1>
        {inserat.ledigname && <p className="text-sm text-[#94A3B8] mt-1">geb. {inserat.ledigname}</p>}
        <div className="flex items-center justify-center gap-3 mt-4 text-sm text-[#64748B]">
          {inserat.geburtsdatum && <span>* {new Date(inserat.geburtsdatum).toLocaleDateString('de-CH')}</span>}
          {inserat.geburtsdatum && inserat.sterbedatum && <span className="text-[#CBD5E1]">—</span>}
          {inserat.sterbedatum && <span>† {new Date(inserat.sterbedatum).toLocaleDateString('de-CH')}</span>}
        </div>
      </div>
    </div>
  );
}

function PersoenlichkeitHero({ inserat }) {
  const { t } = useTranslation();
  return (
    <div className="memorial-hero-persoenlichkeit" data-testid="memorial-hero-persoenlichkeit">
      <div className="max-w-3xl mx-auto px-4 flex items-center gap-8">
        <div className="flex flex-col items-center">
          {inserat.bild_url ? (
            <img src={inserat.bild_url} alt={`${inserat.vorname} ${inserat.nachname}`}
              className="w-28 h-28 md:w-36 md:h-36 rounded-full object-cover border-4 border-white shadow-lg grayscale" />
          ) : (
            <div className="w-28 h-28 md:w-36 md:h-36 rounded-full overflow-hidden border-4 border-white shadow-lg">
              <MemorialPlaceholder 
                vorname={inserat.vorname} 
                nachname={inserat.nachname} 
                kantonCode={inserat.kanton_code}
                sternzeichen={inserat.sternzeichen_name}
                size="lg"
                className="w-full h-full rounded-full"
              />
            </div>
          )}
          {/* Bildquelle Link – nur wenn explizit gesetzt */}
          {inserat.bild_quelle && (
            <a
              href={inserat.bild_quelle}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 text-[10px] text-[#94A3B8] hover:text-[#64748B] transition-colors flex items-center gap-1"
              data-testid="image-source-link"
            >
              Bildquelle
              <ExternalLink className="w-2.5 h-2.5" />
            </a>
          )}
        </div>
        <div>
          <span className="badge-persoenlichkeit mb-2 inline-block">{t('types.persoenlichkeit')}</span>
          <h1 className="font-serif-accent text-3xl md:text-4xl text-[#1E293B]">
            {inserat.vorname} {inserat.nachname}
          </h1>
          {inserat.ledigname && <p className="text-sm text-[#94A3B8] mt-1">geb. {inserat.ledigname}</p>}
          <div className="flex items-center gap-3 mt-3 text-sm text-[#64748B]">
            {inserat.geburtsdatum && <span>* {new Date(inserat.geburtsdatum).toLocaleDateString('de-CH')}</span>}
            {inserat.geburtsdatum && inserat.sterbedatum && <span>—</span>}
            {inserat.sterbedatum && <span>† {new Date(inserat.sterbedatum).toLocaleDateString('de-CH')}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

function IndividuellHero({ inserat }) {
  const { t } = useTranslation();
  return (
    <div className="memorial-hero-individuell" data-testid="memorial-hero-individuell">
      <div className="bg-image" style={{
        backgroundImage: inserat.bild_url
          ? `url(${inserat.bild_url})`
          : `url(https://images.unsplash.com/photo-1682244097336-673f4db39c13?crop=entropy&cs=srgb&fm=jpg&q=85)`
      }} />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/80 via-[#0F172A]/40 to-transparent" />
      <div className="relative z-10 w-full pb-12 pt-32">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <span className="badge-individuell mb-4 inline-block">{t('types.individuell')}</span>
          <h1 className="font-serif-accent text-4xl md:text-5xl text-white drop-shadow-lg">
            {inserat.vorname} {inserat.nachname}
          </h1>
          {inserat.ledigname && <p className="text-sm text-white/70 mt-2">geb. {inserat.ledigname}</p>}
          <div className="flex items-center justify-center gap-3 mt-4 text-sm text-white/80">
            {inserat.geburtsdatum && <span>* {new Date(inserat.geburtsdatum).toLocaleDateString('de-CH')}</span>}
            {inserat.geburtsdatum && inserat.sterbedatum && <span>—</span>}
            {inserat.sterbedatum && <span>† {new Date(inserat.sterbedatum).toLocaleDateString('de-CH')}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

function LetzterGrussHero({ inserat }) {
  const { t } = useTranslation();
  return (
    <div className="memorial-hero-letzter-gruss" data-testid="memorial-hero-letzter-gruss">
      <div className="max-w-3xl mx-auto px-4 text-center relative z-10">
        <span className="badge-letzter-gruss mb-4 inline-block">{t('types.letzter_gruss')}</span>
        {inserat.bild_url ? (
          <img src={inserat.bild_url} alt={`${inserat.vorname} ${inserat.nachname}`}
            className="w-24 h-24 md:w-32 md:h-32 rounded-full object-cover border-4 border-white shadow-lg mx-auto mb-6" />
        ) : (
          <div className="w-24 h-24 md:w-32 md:h-32 rounded-full overflow-hidden mx-auto mb-6 border-4 border-indigo-100 shadow-lg">
            <MemorialPlaceholder 
              vorname={inserat.vorname} 
              nachname={inserat.nachname} 
              kantonCode={inserat.kanton_code}
              sternzeichen={inserat.sternzeichen_name}
              size="lg"
              className="w-full h-full rounded-full"
            />
          </div>
        )}
        <h1 className="font-serif-accent text-3xl md:text-4xl text-[#1E293B]">
          {inserat.vorname} {inserat.nachname}
        </h1>
        {inserat.ledigname && <p className="text-sm text-[#64748B] mt-1">geb. {inserat.ledigname}</p>}
        <div className="flex items-center justify-center gap-3 mt-4 text-sm text-[#64748B]">
          {inserat.geburtsdatum && <span>* {new Date(inserat.geburtsdatum).toLocaleDateString('de-CH')}</span>}
        </div>
        <p className="mt-4 text-sm text-indigo-600 font-medium">In Vorbereitung</p>
      </div>
    </div>
  );
}

export default function MemorialPage() {
  const { id, slug } = useParams();
  const { t, lang } = useTranslation();
  const location = useLocation();
  const [inserat, setInserat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [condolenceOpen, setCondolenceOpen] = useState(false);
  const [viewRecorded, setViewRecorded] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);

  // Determine page language from URL path
  const pageLang = getLanguageFromPath(location.pathname);

  // Load current user to check ownership
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(u => { if (u) setCurrentUserId(u.id); })
      .catch(() => {});
  }, []);

  // Record view (once per session)
  useEffect(() => {
    if (!inserat?.id || viewRecorded) return;
    
    const viewedKey = `viewed_${inserat.id}`;
    if (sessionStorage.getItem(viewedKey)) return;
    
    fetch(`${API}/inserate/${inserat.id}/view`, { method: 'POST' })
      .then(r => r.json())
      .then(data => {
        setInserat(prev => ({ ...prev, view_count: data.view_count }));
        sessionStorage.setItem(viewedKey, '1');
        setViewRecorded(true);
      })
      .catch(console.error);
  }, [inserat?.id, viewRecorded]);

  // Light candle handler - now always opens the form
  const handleLightCandle = () => {
    if (!inserat?.id) return;
    setCondolenceOpen(true);
  };

  useEffect(() => {
    setLoading(true);
    
    // Determine API endpoint based on whether we have slug or id
    let apiUrl;
    if (slug) {
      apiUrl = `${API}/inserate/by-slug/${slug}`;
    } else if (id) {
      apiUrl = `${API}/inserate/${id}`;
    } else {
      setLoading(false);
      return;
    }
    
    fetch(apiUrl)
      .then(r => { if (!r.ok) throw new Error('Not found'); return r.json(); })
      .then(data => {
        setInserat(data);

        // ── SEO meta tags ────────────────────────────────────────────────────
        const fullName = `${data.vorname} ${data.nachname}`;
        const wohnort  = data.wohnort || data.kanton_name;
        const dates    = data.geburtsdatum && data.sterbedatum
          ? `${new Date(data.geburtsdatum).toLocaleDateString('de-CH')} – ${new Date(data.sterbedatum).toLocaleDateString('de-CH')}`
          : '';

        const templates  = META_TEMPLATES[pageLang] || META_TEMPLATES.de;
        const titleStr   = templates.title(fullName, wohnort, data.eintragsart);
        const descStr    = templates.description(fullName, wohnort, dates, data.biografie || '');

        document.title = titleStr;

        // Canonical – always the slug-based URL to avoid duplicate content
        // (/gedenken/:id and /todesanzeigen/:kanton/:slug show same page)
        const kanton = data.kanton_code?.toLowerCase() || 'ch';
        const canonicalUrl = data.slug
          ? `https://verstorben.ch/todesanzeigen/${kanton}/${data.slug}`
          : `https://verstorben.ch/gedenken/${data.id}`;
        setCanonical(canonicalUrl);

        // Standard meta
        setMeta('name', 'description', descStr);
        setMeta('name', 'robots', 'index, follow');

        // Open Graph
        setMeta('property', 'og:title', titleStr);
        setMeta('property', 'og:description', descStr);
        setMeta('property', 'og:type', 'profile');
        setMeta('property', 'og:url', canonicalUrl);
        setMeta('property', 'og:site_name', 'verstorben.ch');
        setMeta('property', 'og:locale', pageLang === 'fr' ? 'fr_CH' : pageLang === 'it' ? 'it_CH' : 'de_CH');
        if (data.bild_url) setMeta('property', 'og:image', data.bild_url);
        setMeta('property', 'profile:first_name', data.vorname);
        setMeta('property', 'profile:last_name', data.nachname);

        // Twitter Card
        setMeta('name', 'twitter:card', data.bild_url ? 'summary_large_image' : 'summary');
        setMeta('name', 'twitter:title', titleStr);
        setMeta('name', 'twitter:description', descStr);
        if (data.bild_url) setMeta('name', 'twitter:image', data.bild_url);

        // JSON-LD: Person schema
        const personSchema = {
          '@context': 'https://schema.org',
          '@type': 'Person',
          name: fullName,
          url: canonicalUrl,
        };
        if (data.geburtsdatum)                personSchema.birthDate   = data.geburtsdatum;
        if (data.sterbedatum)                 personSchema.deathDate   = data.sterbedatum;
        if (data.alter_jahre)                 personSchema.age         = data.alter_jahre;
        if (data.wohnort)                     personSchema.address     = { '@type': 'PostalAddress', addressLocality: data.wohnort, addressCountry: 'CH' };
        if (data.heimatort)                   personSchema.birthPlace  = { '@type': 'Place', name: data.heimatort };
        if (data.nationalitaet)               personSchema.nationality = data.nationalitaet;
        if (data.biografie)                   personSchema.description = data.biografie.substring(0, 500);
        if (data.bild_url)                    personSchema.image       = data.bild_url;
        if (data.wikipedia_url)               personSchema.sameAs      = data.wikipedia_url;
        if (data.berufe?.length)              personSchema.jobTitle    = data.berufe.join(', ');
        setJsonLd('ld-person', personSchema);

        // JSON-LD: BreadcrumbList
        setJsonLd('ld-breadcrumb', {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Startseite', item: 'https://verstorben.ch' },
            { '@type': 'ListItem', position: 2, name: 'Suche',      item: 'https://verstorben.ch/suche' },
            { '@type': 'ListItem', position: 3, name: fullName,     item: canonicalUrl },
          ],
        });
      })
      .catch(console.error)
      .finally(() => setLoading(false));

    return () => {
      document.title = 'Verstorben.ch | Todesanzeigen und Gedenkportal Schweiz';
      ['ld-person', 'ld-breadcrumb'].forEach(elId => document.getElementById(elId)?.remove());
      document.querySelector('link[rel="canonical"]')?.remove();
    };
  }, [id, slug, pageLang]);

  // Check claim status
  const [claimStatus, setClaimStatus] = useState(null);
  
  useEffect(() => {
    if (!inserat?.id) return;
    fetch(`${API}/claim-requests/status/inserat/${inserat.id}`)
      .then(r => r.json())
      .then(setClaimStatus)
      .catch(console.error);
  }, [inserat?.id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-[#1E293B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!inserat) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <p className="text-[#94A3B8]">{t('common.error')}</p>
        <Link to="/suche" className="text-sm text-[#475569] hover:text-[#1E293B] mt-4 inline-block">{t('common.back')}</Link>
      </div>
    );
  }

  // Map old values to new values (for backward compatibility during migration)
  const typeMapping = {
    'amtlich': 'publiziert',
    'prominent': 'persoenlichkeit',
    'premium': 'individuell'
  };
  const normalizedType = typeMapping[inserat.eintragsart] || inserat.eintragsart;

  const HeroComponent = normalizedType === 'individuell' ? IndividuellHero
    : normalizedType === 'persoenlichkeit' ? PersoenlichkeitHero 
    : normalizedType === 'letzter_gruss' ? LetzterGrussHero 
    : PubliziertHero;

  return (
    <div className="page-enter" data-testid="memorial-page">
      {/* Back link */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <Link to="/suche" className="flex items-center gap-1 text-sm text-[#475569] hover:text-[#1E293B] transition-colors" data-testid="back-to-search">
          <ArrowLeft className="w-4 h-4" /> {t('common.back')}
        </Link>
      </div>

      {/* Hero - varies by type */}
      <HeroComponent inserat={inserat} />

      {/* View & Candle Counter Bar + Share */}
      <div className="max-w-3xl mx-auto px-4 pt-6">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 text-sm text-[#94A3B8]" data-testid="memorial-counters">
          {/* Besuche & Kerzen - immer auf einer Zeile */}
          <div className="flex items-center gap-4 sm:gap-6">
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              <span>{inserat.view_count || 0} {t('memorial.views')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Flame className="w-4 h-4 text-[#C5A059]" />
              <span>{inserat.candle_count || 0} {t('memorial.candles')}</span>
            </div>
          </div>
          
          {/* Dezente Share Buttons - auf Mobile darunter */}
          <div className="flex items-center gap-1 sm:ml-2 sm:pl-4 sm:border-l border-[#E2E8F0]" data-testid="social-share-buttons">
            <span className="text-xs text-[#CBD5E1] mr-1">{t('memorial.share')}</span>
            
            {/* WhatsApp */}
            <a
              href={`https://wa.me/?text=${encodeURIComponent(`${inserat.vorname} ${inserat.nachname} - Gedenkseite auf verstorben.ch: ${window.location.href}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-7 h-7 flex items-center justify-center rounded-full text-[#94A3B8] hover:text-[#25D366] hover:bg-[#25D366]/10 transition-colors"
              data-testid="share-whatsapp"
              title="WhatsApp"
            >
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </a>
            
            {/* Facebook */}
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-7 h-7 flex items-center justify-center rounded-full text-[#94A3B8] hover:text-[#1877F2] hover:bg-[#1877F2]/10 transition-colors"
              data-testid="share-facebook"
              title="Facebook"
            >
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>
            
            {/* Twitter/X */}
            <a
              href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(`Gedenkseite für ${inserat.vorname} ${inserat.nachname} auf verstorben.ch`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-7 h-7 flex items-center justify-center rounded-full text-[#94A3B8] hover:text-[#0F1419] hover:bg-[#0F1419]/10 transition-colors"
              data-testid="share-twitter"
              title="X (Twitter)"
            >
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </a>
            
            {/* Email */}
            <a
              href={`mailto:?subject=${encodeURIComponent(`Gedenkseite: ${inserat.vorname} ${inserat.nachname}`)}&body=${encodeURIComponent(`Ich möchte diese Gedenkseite mit dir teilen:\n\n${inserat.vorname} ${inserat.nachname}\n${window.location.href}`)}`}
              className="w-7 h-7 flex items-center justify-center rounded-full text-[#94A3B8] hover:text-[#64748B] hover:bg-[#64748B]/10 transition-colors"
              data-testid="share-email"
              title="E-Mail"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
              </svg>
            </a>
            
            {/* Copy Link */}
            <button
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(window.location.href);
                  setLinkCopied(true);
                  setTimeout(() => setLinkCopied(false), 2000);
                } catch (err) {
                  const textArea = document.createElement('textarea');
                  textArea.value = window.location.href;
                  document.body.appendChild(textArea);
                  textArea.select();
                  document.execCommand('copy');
                  document.body.removeChild(textArea);
                  setLinkCopied(true);
                  setTimeout(() => setLinkCopied(false), 2000);
                }
              }}
              className={`w-7 h-7 flex items-center justify-center rounded-full transition-colors ${
                linkCopied 
                  ? 'text-[#22C55E] bg-[#22C55E]/10' 
                  : 'text-[#94A3B8] hover:text-[#64748B] hover:bg-[#64748B]/10'
              }`}
              data-testid="share-copy-link"
              title={linkCopied ? t('memorial.link_copied') : t('memorial.copy_link')}
            >
              {linkCopied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-12 relative">
        {/* Zodiac Background Icon - subtle watermark style */}
        {inserat.sternzeichen && ZODIAC_ICONS[inserat.sternzeichen] && (
          <div className="absolute top-0 right-0 w-64 h-64 text-[#C5A059] opacity-[0.06] pointer-events-none" data-testid="zodiac-background">
            {ZODIAC_ICONS[inserat.sternzeichen]}
          </div>
        )}

        {/* Personal Details */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
          {inserat.alter_jahre && (
            <div className="p-4 bg-white rounded-sm border border-[#E2E8F0]" data-testid="detail-age">
              <p className="text-xs text-[#94A3B8] uppercase tracking-wider mb-1">{t('memorial.age')}</p>
              <p className="font-heading text-xl text-[#1E293B]">{inserat.alter_jahre} {t('memorial.years')}</p>
            </div>
          )}
          {inserat.wohnort && (
            <div className="p-4 bg-white rounded-sm border border-[#E2E8F0]" data-testid="detail-residence">
              <p className="text-xs text-[#94A3B8] uppercase tracking-wider mb-1">{t('memorial.residence')}</p>
              <p className="text-sm text-[#1E293B] flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-[#94A3B8]" /> {inserat.wohnort}
              </p>
            </div>
          )}
          {inserat.heimatort && (
            <div className="p-4 bg-white rounded-sm border border-[#E2E8F0]" data-testid="detail-hometown">
              <p className="text-xs text-[#94A3B8] uppercase tracking-wider mb-1">{t('memorial.hometown')}</p>
              <p className="text-sm text-[#1E293B] flex items-center gap-1.5">
                <Home className="w-3.5 h-3.5 text-[#94A3B8]" /> {inserat.heimatort}
              </p>
            </div>
          )}
          {inserat.kanton_name && (
            <div className="p-4 bg-white rounded-sm border border-[#E2E8F0]" data-testid="detail-canton">
              <p className="text-xs text-[#94A3B8] uppercase tracking-wider mb-1">{t('memorial.canton')}</p>
              <p className="text-sm text-[#1E293B] flex items-center gap-1.5">
                <Flag className="w-3.5 h-3.5 text-[#94A3B8]" /> {inserat.kanton_name}
              </p>
            </div>
          )}
          {inserat.nationalitaet && (
            <div className="p-4 bg-white rounded-sm border border-[#E2E8F0]" data-testid="detail-nationality">
              <p className="text-xs text-[#94A3B8] uppercase tracking-wider mb-1">{t('memorial.nationality')}</p>
              <p className="text-sm text-[#1E293B] flex items-center gap-1.5">
                <Globe className="w-3.5 h-3.5 text-[#94A3B8]" /> {inserat.nationalitaet}
              </p>
            </div>
          )}
          {inserat.sternzeichen && (
            <Link 
              to={`/suche?sternzeichen=${encodeURIComponent(inserat.sternzeichen)}`}
              className="p-4 bg-white rounded-sm border border-[#E2E8F0] hover:border-[#C5A059] hover:bg-[#FFFBF5] transition-colors group" 
              data-testid="detail-zodiac"
            >
              <p className="text-xs text-[#94A3B8] uppercase tracking-wider mb-1">{t('memorial.zodiac')}</p>
              <p className="text-sm text-[#1E293B] flex items-center gap-1.5 group-hover:text-[#C5A059]">
                <span className="w-4 h-4 text-[#C5A059]">
                  {ZODIAC_ICONS[inserat.sternzeichen] || '★'}
                </span>
                {inserat.sternzeichen}
              </p>
            </Link>
          )}
          {inserat.wikipedia_url && (
            <a 
              href={inserat.wikipedia_url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-4 bg-white rounded-sm border border-[#E2E8F0] hover:border-[#3B82F6] hover:bg-[#EFF6FF] transition-colors group" 
              data-testid="detail-wikipedia"
            >
              <p className="text-xs text-[#94A3B8] uppercase tracking-wider mb-1">{t('memorial.wikipedia')}</p>
              <p className="text-sm text-[#1E293B] flex items-center gap-1.5 group-hover:text-[#3B82F6]">
                <svg className="w-4 h-4 text-[#3B82F6]" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm6.918 6h-3.215c-.188-1.424-.42-2.65-.71-3.614A8.073 8.073 0 0118.918 8zM12 4.042c.626 1.182 1.065 2.635 1.32 3.958h-2.64c.255-1.323.694-2.776 1.32-3.958zm-2.993.344c-.29.964-.522 2.19-.71 3.614H5.082a8.073 8.073 0 013.925-3.614zM4 12c0-.69.088-1.36.252-2h3.544a21.735 21.735 0 000 4H4.252A7.965 7.965 0 014 12zm1.082 4h3.215c.188 1.424.42 2.65.71 3.614A8.073 8.073 0 015.082 16zM12 19.958c-.626-1.182-1.065-2.635-1.32-3.958h2.64c-.255 1.323-.694 2.776-1.32 3.958zm2.993-.344c.29-.964.522-2.19.71-3.614h3.215a8.073 8.073 0 01-3.925 3.614zM16.204 14a21.735 21.735 0 000-4h3.544c.164.64.252 1.31.252 2s-.088 1.36-.252 2h-3.544z"/>
                </svg>
                {t('memorial.read_article')}
                <ExternalLink className="w-3 h-3 ml-auto opacity-50 group-hover:opacity-100" />
              </p>
            </a>
          )}
          {normalizedType === 'persoenlichkeit' && inserat.todesursache && (
            <div className="p-4 bg-white rounded-sm border border-[#E2E8F0]" data-testid="detail-cause-of-death">
              <p className="text-xs text-[#94A3B8] uppercase tracking-wider mb-1">{t('memorial.cause_of_death')}</p>
              <p className="text-sm text-[#475569]">{inserat.todesursache}</p>
            </div>
          )}
        </div>

        <Separator className="my-8 bg-[#E2E8F0]" />

        {/* Biography */}
        {inserat.biografie && (
          <section className="mb-8" data-testid="biography-section">
            <h2 className="font-heading text-xl text-[#1E293B] mb-4">{t('memorial.biography')}</h2>
            <div className="text-[#475569] leading-relaxed whitespace-pre-line">
              {(lang === 'fr' && inserat.biografie_fr) || (lang === 'it' && inserat.biografie_it) || inserat.biografie}
            </div>
          </section>
        )}

        {/* Original content */}
        {inserat.content && (
          <section className="mb-8" data-testid="content-section">
            <h2 className="font-heading text-xl text-[#1E293B] mb-4">
              {normalizedType === 'persoenlichkeit' ? t('memorial.achievements') : t('memorial.official_notice')}
            </h2>
            <div className="bg-[#F9FAFB] border border-[#E2E8F0] rounded-sm p-6">
              <p className="text-[#475569] leading-relaxed whitespace-pre-line text-sm">
                {(lang === 'fr' && inserat.content_fr) || (lang === 'it' && inserat.content_it) || inserat.content}
              </p>
            </div>
          </section>
        )}

        {/* Professions */}
        {inserat.berufe && inserat.berufe.length > 0 && (
          <div className="flex items-center gap-2 mb-6" data-testid="professions">
            <Briefcase className="w-4 h-4 text-[#94A3B8]" />
            <span className="text-sm text-[#475569]">{inserat.berufe.join(', ')}</span>
          </div>
        )}

        <Separator className="my-8 bg-[#E2E8F0]" />

        {/* Condolence Actions */}
        {inserat.condolences_enabled !== false && (
          <>
            <div className="bg-[#FDF8F0] border border-[#E8DCC2] rounded-sm p-6 text-center mb-8" data-testid="condolence-section">
              <Flame className="w-10 h-10 text-[#C5A059] mx-auto mb-3" />
              <h3 className="font-heading text-lg text-[#1E293B] mb-2">{t('memorial.condolence_title')}</h3>
              <p className="text-sm text-[#475569] mb-5 max-w-md mx-auto">
                {t('memorial.condolence_desc')}
              </p>
              <Button
                onClick={handleLightCandle}
                className="bg-[#C5A059] hover:bg-[#B08D45] text-white rounded-sm px-8"
                data-testid="condolence-btn"
              >
                <Flame className="w-4 h-4 mr-2" />
                {t('memorial.condolence_btn')}
              </Button>
            </div>

            {/* Condolence List */}
            <CondolenceList inseratId={inserat.id} />
          </>
        )}

        <Separator className="my-8 bg-[#E2E8F0]" />

        {/* Claim Section */}
        {inserat.status !== 'claimed' && claimStatus?.status !== 'pending' && (
          <div className="bg-white border border-[#E2E8F0] rounded-sm p-6 text-center" data-testid="claim-section">
            <Heart className="w-8 h-8 text-[#475569] mx-auto mb-3" />
            <p className="text-sm text-[#475569] mb-4 max-w-md mx-auto">{t('memorial.claim_info')}</p>
            <Link to={`/beanspruchen/${inserat.id}`}>
              <Button
                variant="outline"
                className="rounded-sm text-sm"
                data-testid="claim-button"
              >
                {t('memorial.claim')}
              </Button>
            </Link>
          </div>
        )}
        {claimStatus?.status === 'pending' && inserat.status !== 'claimed' && (
          <div className="bg-[#FEF3C7] border border-[#FCD34D] rounded-sm p-4 text-center" data-testid="claim-pending-badge">
            <p className="text-sm text-[#D97706] font-medium">{t('memorial.claim_pending')}</p>
          </div>
        )}
        {inserat.status === 'claimed' && (
          <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-sm p-4 flex items-center justify-between gap-3" data-testid="claimed-badge">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-[#059669] flex-shrink-0" />
              <p className="text-sm text-[#059669] font-medium">{t('memorial.claimed_badge')}</p>
            </div>
            {currentUserId && inserat.owner_id === currentUserId && (
              <Link to={`/inserat/${inserat.id}/bearbeiten`}>
                <Button size="sm" variant="outline" className="border-[#BBF7D0] text-[#059669] hover:bg-[#DCFCE7] gap-1.5">
                  <Pencil className="w-3.5 h-3.5" /> {t('memorial.edit_btn')}
                </Button>
              </Link>
            )}
          </div>
        )}

        {/* Donation Block — nur bei nicht-geclaimten Seiten */}
        {inserat.status !== 'claimed' && <div className="mt-6 bg-gradient-to-br from-[#FFFBF5] to-white border border-[#E8DCC2] rounded-sm p-6 text-center" data-testid="donate-section">
          <p className="text-xs text-[#C5A059] uppercase tracking-[0.15em] font-medium mb-2">{t('memorial.donate_title')}</p>
          <p className="text-sm text-[#64748B] mb-4 max-w-sm mx-auto">{t('memorial.donate_desc')}</p>
          <Link to={lang === 'fr' ? '/don' : lang === 'it' ? '/donazione' : '/spenden'}>
            <Button variant="outline" className="rounded-sm border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059] hover:text-white transition-colors text-sm px-6">
              {t('memorial.donate_btn')}
            </Button>
          </Link>
        </div>}
      </div>

      {/* Condolence Dialog */}
      <CondolenceDialog
        open={condolenceOpen}
        onOpenChange={setCondolenceOpen}
        inseratId={inserat.id}
        inseratName={`${inserat.vorname} ${inserat.nachname}`}
        onSuccess={() => {
          // Refresh page data
          window.location.reload();
        }}
      />
    </div>
  );
}
